import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, interval, takeWhile, switchMap, Subject, map } from 'rxjs';
import { ConfigService } from '../config/config.service';
import { AuthService } from '../auth/auth.service';
import {
  SimulationDefinitionDto,
  CreateSimulationDefinitionRequest,
  UpdateSimulationDefinitionRequest,
  SimulationExecutionDto,
  SimulationExecutionProgressDto,
  SimulationExecutionSummaryDto,
  ExecuteSimulationRequest,
  ExecutionFailure,
  SimulationProfile,
  HealthCheckResponse,
  RoundValidationResult,
  LegacyReplayComparisonResult,
  DatabaseRestorePreview,
  DatabaseRestoreResult,
  DatabaseRestoreOptions,
} from '../../models/simulator.models';

/**
 * Service for communicating with the Simulator API
 */
@Injectable({
  providedIn: 'root',
})
export class SimulatorService {
  private simulatorBasePath: string;
  private progressPollingSubscription$ = new Subject<void>();
  private authService = inject(AuthService);

  constructor(private http: HttpClient, private configService: ConfigService) {
    this.simulatorBasePath =
      this.configService.config?.simulatorBasePath || '';
  }

  // ===== Health Check =====

  checkHealth(): Observable<HealthCheckResponse> {
    return this.http
      .get<HealthCheckResponse>(`${this.simulatorBasePath}/api/system/health`)
      .pipe(
        map((resp) => ({
          ...resp,
          status:
            resp.status && resp.status.toLowerCase() === 'healthy'
              ? 'Healthy'
              : resp.status,
        }))
      );
  }

  // ===== Definition Management =====

  createDefinition(
    request: CreateSimulationDefinitionRequest
  ): Observable<SimulationDefinitionDto> {
    const userName = this.authService.userName();
    return this.http.post<SimulationDefinitionDto>(
      `${this.simulatorBasePath}/api/simulations/definitions`,
      { ...request, createdBy: userName }
    );
  }

  getAllDefinitions(): Observable<SimulationDefinitionDto[]> {
    return this.http.get<SimulationDefinitionDto[]>(
      `${this.simulatorBasePath}/api/simulations/definitions`
    );
  }

  getDefinitionById(id: string): Observable<SimulationDefinitionDto> {
    return this.http.get<SimulationDefinitionDto>(
      `${this.simulatorBasePath}/api/simulations/definitions/${id}`
    );
  }

  updateDefinition(
    id: string,
    request: UpdateSimulationDefinitionRequest
  ): Observable<SimulationDefinitionDto> {
    const userName = this.authService.userName();
    return this.http.put<SimulationDefinitionDto>(
      `${this.simulatorBasePath}/api/simulations/definitions/${id}`,
      { ...request, modifiedBy: userName }
    );
  }

  deleteDefinition(id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.simulatorBasePath}/api/simulations/definitions/${id}`
    );
  }

  // ===== Profiles =====

  getProfiles(): Observable<SimulationProfile[]> {
    return this.http.get<SimulationProfile[]>(
      `${this.simulatorBasePath}/api/simulations/profiles`
    );
  }

  // ===== Execution Management =====

  executeSimulation(
    request: ExecuteSimulationRequest
  ): Observable<SimulationExecutionDto> {
    const userName = this.authService.userName();
    return this.http.post<SimulationExecutionDto>(
      `${this.simulatorBasePath}/api/simulations/execute`,
      { ...request, startedBy: userName }
    );
  }

  getExecution(id: string): Observable<SimulationExecutionDto> {
    return this.http.get<SimulationExecutionDto>(
      `${this.simulatorBasePath}/api/simulations/executions/${id}`
    );
  }

  getAllExecutions(): Observable<SimulationExecutionDto[]> {
    return this.http.get<SimulationExecutionDto[]>(
      `${this.simulatorBasePath}/api/simulations/executions`
    );
  }

  getExecutionProgress(
    executionId: string
  ): Observable<SimulationExecutionProgressDto> {
    return this.http.get<SimulationExecutionProgressDto>(
      `${this.simulatorBasePath}/api/simulations/executions/${executionId}/progress`
    );
  }

  getExecutionSummary(executionId: string): Observable<SimulationExecutionSummaryDto> {
    return this.http.get<SimulationExecutionSummaryDto>(
      `${this.simulatorBasePath}/api/simulations/executions/${executionId}/summary`
    );
  }

  validateRound(executionId: string, roundNumber: number): Observable<RoundValidationResult> {
    return this.http.post<RoundValidationResult>(
      `${this.simulatorBasePath}/api/simulations/${executionId}/validate-round/${roundNumber}`,
      null
    );
  }

  compareLegacyReplay(executionId: string, roundNumber: number): Observable<LegacyReplayComparisonResult> {
    return this.http.post<LegacyReplayComparisonResult>(
      `${this.simulatorBasePath}/api/simulations/${executionId}/compare-legacy-replay/${roundNumber}`,
      null
    );
  }

  // ===== Database Restore =====

  getDatabaseRestorePreview(): Observable<DatabaseRestorePreview> {
    return this.http.get<DatabaseRestorePreview>(
      `${this.simulatorBasePath}/api/database/restore/preview`
    );
  }

  restoreDatabase(options: DatabaseRestoreOptions, reason?: string): Observable<DatabaseRestoreResult> {
    let params = new HttpParams()
      .set('ConfirmRestore', 'true')
      .set('RestoreScoring', String(options.restoreScoring))
      .set('ClearSimulator', String(options.clearSimulator));
    if (reason) {
      params = params.set('Reason', reason);
    }
    return this.http.post<DatabaseRestoreResult>(
      `${this.simulatorBasePath}/api/database/restore`,
      null,
      { params }
    );
  }

  getExecutionFailures(executionId: string): Observable<ExecutionFailure[]> {
    return this.http.get<ExecutionFailure[]>(
      `${this.simulatorBasePath}/api/simulations/executions/${executionId}/failures`
    );
  }

  pauseExecution(
    executionId: string,
    reason?: string
  ): Observable<SimulationExecutionDto> {
    return this.http.post<SimulationExecutionDto>(
      `${this.simulatorBasePath}/api/simulations/pause/${executionId}`,
      reason || null
    );
  }

  resumeExecution(executionId: string): Observable<SimulationExecutionDto> {
    return this.http.post<SimulationExecutionDto>(
      `${this.simulatorBasePath}/api/simulations/resume/${executionId}`,
      null
    );
  }

  stopExecution(
    executionId: string,
    reason?: string
  ): Observable<SimulationExecutionDto> {
    return this.http.post<SimulationExecutionDto>(
      `${this.simulatorBasePath}/api/simulations/stop/${executionId}`,
      reason || null
    );
  }

  // ===== Dry Run Validation =====

  runDryRun(request: ExecuteSimulationRequest): Observable<SimulationExecutionDto> {
    const userName = this.authService.userName();
    return this.http.post<SimulationExecutionDto>(
      `${this.simulatorBasePath}/api/simulations/execute`,
      { ...request, dryRun: true, startedBy: userName }
    );
  }

  // ===== Progress Polling =====

  /**
   * Start polling for execution progress at the specified interval
   * Returns an observable that emits progress updates
   * Automatically stops when execution reaches terminal state
   */
  startProgressPolling(
    executionId: string,
    intervalMs: number = 2000
  ): Observable<SimulationExecutionProgressDto> {
    return interval(intervalMs).pipe(
      switchMap(() => this.getExecutionProgress(executionId)),
      takeWhile(
        (progress) =>
          progress.status === 'Running' ||
          progress.status === 'Pending',
        true // Include the last emission when status changes to terminal
      ),
      takeWhile(() => !this.progressPollingSubscription$.closed)
    );
  }

  /**
   * Stop any active progress polling
   */
  stopProgressPolling(): void {
    this.progressPollingSubscription$.next();
  }

  // ===== Continuous Execution / Round Transitions =====

  /**
   * Acknowledge a round transition.
   * Called after the Admin UI has performed round closure and activation in vNext API.
   * On success, the Simulator will prepare for the next round and resume execution.
   */
  acknowledgeRoundTransition(
    executionId: string,
    isSuccess: boolean,
    nextRoundNumber: number,
    message?: string
  ): Observable<SimulationExecutionDto> {
    return this.http.post<SimulationExecutionDto>(
      `${this.simulatorBasePath}/api/simulations/executions/${executionId}/acknowledge-round-transition`,
      {
        isSuccess,
        nextRoundNumber,
        message
      }
    );
  }
}
